import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import scheduleAPI, { SchedulePaymentChargeResponse } from "apis/ScheduleAPI";
import { FC, useState } from "react";
import toast from "react-hot-toast";

interface FinanceiroTabProps {
  scheduleId: string;
  paymentCharge: SchedulePaymentChargeResponse | null | undefined;
  onChargeCreated: () => Promise<void>;
}

const STATUS_LABELS: Record<string, string> = {
  PENDING: "Aguardando pagamento",
  PAID: "Pago",
  EXPIRED: "Expirado",
  CANCELED: "Cancelado",
};

const STATUS_COLORS: Record<
  string,
  "warning" | "success" | "error" | "default"
> = {
  PENDING: "warning",
  PAID: "success",
  EXPIRED: "error",
  CANCELED: "default",
};

const canGenerateCharge = (
  charge: SchedulePaymentChargeResponse | null | undefined
): boolean =>
  !charge || charge.status === "EXPIRED" || charge.status === "CANCELED";

const FinanceiroTab: FC<FinanceiroTabProps> = ({
  scheduleId,
  paymentCharge,
  onChargeCreated,
}) => {
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      await scheduleAPI.createCharge(scheduleId);
      toast.success("Cobrança gerada com sucesso!");
      await onChargeCreated();
    } catch {
      toast.error("Erro ao gerar cobrança.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!paymentCharge?.paymentUrl) return;
    navigator.clipboard
      .writeText(paymentCharge.paymentUrl)
      .then(() => toast.success("Link copiado!"));
  };

  const handleOpen = () => {
    if (!paymentCharge?.paymentUrl) return;
    window.open(paymentCharge.paymentUrl, "_blank");
  };

  return (
    <Box>
      {paymentCharge ? (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              Status:
            </Typography>
            <Chip
              label={STATUS_LABELS[paymentCharge.status] ?? paymentCharge.status}
              color={STATUS_COLORS[paymentCharge.status] ?? "default"}
              size="small"
            />
          </Stack>

          {paymentCharge.status === "PENDING" && paymentCharge.expiresAt && (
            <Typography variant="body2" color="text.secondary">
              Expira em:{" "}
              {new Date(paymentCharge.expiresAt).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Typography>
          )}

          {paymentCharge.status === "PENDING" && paymentCharge.paymentUrl && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={handleOpen}
              >
                Abrir link
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopy}
              >
                Copiar link
              </Button>
            </Stack>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Nenhuma cobrança gerada para este agendamento.
        </Typography>
      )}

      {canGenerateCharge(paymentCharge) && (
        <Box mt={2}>
          <Button
            variant="contained"
            size="small"
            onClick={handleGenerate}
            disabled={loading}
          >
            {paymentCharge ? "Regerar cobrança" : "Gerar cobrança"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FinanceiroTab;
